import { useEffect, useRef } from 'react';
import { PLAN_LIST, formatPlanLimit } from '@logpulse/types/plans';
import {
  SITE_ORIGIN,
  SITE_LOCALE,
  HREFLANG_TAGS,
  LOCALE_ORIGINS,
} from '@/lib/site-config';

interface JsonLdProps {
  data: Record<string, unknown>;
}

/**
 * Injects a JSON-LD script tag into the document head.
 * Removes it on unmount to avoid duplicates during navigation.
 */
export function JsonLd({ data }: JsonLdProps) {
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    scriptRef.current = script;

    return () => {
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
    };
  }, [JSON.stringify(data)]);

  return null;
}

/** Helper to build a BlogPosting JSON-LD object */
export function blogPostingJsonLd(post: {
  title: string;
  excerpt: string;
  author: string;
  date: string;
  slug: string;
  category: string;
  readTime: string;
  content: { paragraphs: string[] }[];
}) {
  const isoDate = new Date(post.date).toISOString();
  const wordCount = post.content.reduce(
    (acc, section) => acc + section.paragraphs.join(' ').split(/\s+/).length,
    0,
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    author: {
      '@type': 'Person',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'LogPulse',
      url: 'https://logpulse.io',
      logo: {
        '@type': 'ImageObject',
        url: 'https://logpulse.io/favicon.svg',
      },
    },
    datePublished: isoDate,
    dateModified: isoDate,
    url: `https://logpulse.io/blog/${post.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://logpulse.io/blog/${post.slug}`,
    },
    articleSection: post.category,
    wordCount,
    inLanguage: 'en-US',
    isAccessibleForFree: true,
    image: 'https://logpulse.io/og-image.jpg',
  };
}

/** Helper to build a BreadcrumbList JSON-LD object */
export function breadcrumbJsonLd(
  items: { name: string; url: string }[],
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Build a FAQPage JSON-LD object from visible Q&A pairs. The questions/answers
 * MUST also be rendered visibly on the page (Google's FAQ rich-result policy),
 * see the FaqSection component, which renders both.
 */
export function faqPageJsonLd(items: { q: string; a: string }[], inLanguage?: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    ...(inLanguage ? { inLanguage } : {}),
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    })),
  };
}

// ──────────────────────────────────────────────
// Sitewide identity graph: Organization, WebSite, SoftwareApplication.
//
// Rendered once at the app root (see App.tsx) so every prerendered page
// carries a stable, cross-linked entity graph. The Organization and
// SoftwareApplication @ids are pinned to the canonical .io origin so all four
// ccTLD builds reference the SAME entity node (better for the AI/search
// knowledge graph); the WebSite node is per-domain. No SearchAction is
// emitted. The marketing site has no site-search endpoint and faking one
// would be invalid markup.
// ──────────────────────────────────────────────

const ORG_ID = `${LOCALE_ORIGINS.en}/#organization`;
const SOFTWARE_ID = `${LOCALE_ORIGINS.en}/#software`;

/** Organization node, stable across every locale build. */
export function organizationJsonLd() {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: 'LogPulse',
    legalName: 'LayerCares B.V.',
    url: LOCALE_ORIGINS.en,
    logo: {
      '@type': 'ImageObject',
      url: `${LOCALE_ORIGINS.en}/android-chrome-512x512.png`,
      width: 512,
      height: 512,
    },
    image: `${LOCALE_ORIGINS.en}/og-image.jpg`,
    email: 'support@logpulse.io',
    description:
      'LogPulse is an AI-assisted, EU-hosted log management and security monitoring (SIEM) platform. Search logs in natural language, investigate incidents with an AI Investigator, detect anomalies, and run a risk-based SIEM on one engine. Coding agents connect over MCP to watch the apps they built. GDPR-compliant and hosted in the EU.',
    areaServed: 'EU',
    sameAs: [
      LOCALE_ORIGINS.nl,
      LOCALE_ORIGINS.de,
      LOCALE_ORIGINS.fr,
      // Authoritative entity reference, closes the bidirectional link with the
      // Wikidata item (which lists logpulse.io as official website), a strong
      // disambiguation signal against the unrelated "LogPulse" namesakes.
      'https://www.wikidata.org/wiki/Q140374007',
      'https://x.com/logpulse_io',
      'https://linkedin.com/company/logpulse',
    ],
  };
}

/** WebSite node, per-domain, linked to the canonical Organization. */
export function websiteJsonLd() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_ORIGIN}/#website`,
    name: 'LogPulse',
    url: SITE_ORIGIN,
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    publisher: { '@id': ORG_ID },
  };
}

/**
 * SoftwareApplication node with one Offer per public plan, sourced from
 * PLAN_LIST so prices never drift from billing. The legacy `starter` tier is
 * excluded to match the live pricing page; Enterprise is custom-priced and
 * surfaced as a contact Offer without a numeric price.
 */
export function softwareApplicationJsonLd() {
  const offers = PLAN_LIST.filter((plan) => plan.id !== 'starter').map((plan) => ({
    '@type': 'Offer',
    name: `LogPulse ${plan.name}`,
    description: `${formatPlanLimit(plan.id)} ingest, ${plan.retentionDays}-day retention`,
    price: String(plan.priceEur),
    priceCurrency: 'EUR',
    category: plan.priceEur === 0 ? 'free' : 'subscription',
    url: `${LOCALE_ORIGINS.en}/pricing/compare`,
    availability: 'https://schema.org/InStock',
    ...(plan.priceEur > 0
      ? {
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: String(plan.priceEur),
            priceCurrency: 'EUR',
            referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'MONTH' },
          },
        }
      : {}),
  }));

  return {
    '@type': 'SoftwareApplication',
    '@id': SOFTWARE_ID,
    name: 'LogPulse',
    applicationCategory: 'DeveloperApplication',
    applicationSubCategory: 'Log Management, Observability & SIEM',
    operatingSystem: 'Web',
    url: LOCALE_ORIGINS.en,
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    description:
      'AI-assisted log management and SIEM platform. Natural-language log search with the LPQL query language, an AI Investigator for root-cause analysis, anomaly detection, risk-based security monitoring with MITRE ATT&CK detections, service intelligence, and visual pipelines, on one EU-hosted, GDPR-compliant engine.',
    publisher: { '@id': ORG_ID },
    screenshot: `${LOCALE_ORIGINS.en}/og-image.jpg`,
    featureList: [
      'AI log search (natural language to LPQL)',
      'AI Investigator for root-cause analysis',
      'Anomaly detection',
      'Security Monitoring (SIEM) with MITRE ATT&CK detections',
      'Risk-based alerting and UEBA',
      'Service Intelligence (service health and KPIs)',
      'Visual log pipelines',
      'MCP server for AI coding agents (Claude Code, Cursor, Codex) with propose-and-approve changes',
      'Interactive MCP widgets in Claude and ChatGPT (search, briefing, incident room, proposals)',
      'Production monitoring for apps built with AI coding agents',
      'Free plan with security monitoring, observability and MCP',
      'EU data residency (GDPR-compliant)',
    ],
    offers,
  };
}

/**
 * Sitewide identity graph. Rendered once at the app root so the Organization,
 * WebSite, and SoftwareApplication entities appear (cross-linked by @id) on
 * every prerendered page.
 */
export function SiteJsonLd() {
  const graph = {
    '@context': 'https://schema.org',
    '@graph': [organizationJsonLd(), websiteJsonLd(), softwareApplicationJsonLd()],
  };
  return <JsonLd data={graph} />;
}
